/**
 * As RATs do Negócio, dentro da ficha (6.3, FR-48, FR-49, FR-51, FR-52, FR-62, UX-DR12).
 *
 * **Ele é a resposta à última ausência que a 3.10 registrou por escrito.** Aquela história deixou o
 * cartão "RAT" como um `CardReservado` com a frase "A RAT chega no Épico 6"; a 6.1 abriu a rota, a
 * 6.2 ligou o aviso por e-mail, e nenhuma das duas tinha tela. Este cartão é o Épico 6 chegando ao
 * lugar onde o Vendedor já trabalha (FR-51) — e com ele o último slot reservado da ficha some.
 *
 * **A RAT é agrupada pelo `numero_do_pedido` de cada linha, e nunca pelo do Negócio.** É essa
 * escolha que faz "um Negócio com dois Pedidos mostra as RATs de cada um separadamente" (FR-51)
 * valer: o Negócio tem **uma** coluna de Pedido, e ela pode mudar; cada RAT carrega o snapshot que
 * a abertura copiou (6.1). Agrupar pelo campo do Negócio faria as reclamações de um Pedido antigo
 * migrarem de grupo no dia em que o número mudasse — reescrevendo o passado na tela.
 *
 * **O agrupamento é da tela, e não do serviço.** O `GET` devolve uma lista plana ordenada da mais
 * nova para a mais velha; um envelope já agrupado congelaria no contrato uma decisão de
 * apresentação. Os grupos saem na ordem em que o primeiro item de cada um aparece — o Pedido com a
 * reclamação mais recente vem primeiro, que é a mesma ordem da lista.
 *
 * **Duas escritas, e as duas passam por guarda do FR-61-A.** Abrir exige `abrir_rat`; mover o
 * status exige `alterar_status_da_rat`. A checagem daqui é por **presença de chave** em
 * `sessao.permissoes`, o molde de `podeEditar` da ficha: só as não negadas viajam na claim, e
 * `limitada` passa pela guarda da rota porque o recorte de `limitada` é de **linha** (a RLS). E ela
 * é **conveniência**, nunca a guarda: quem decide é o serviço, que responde `403` a quem não a tem
 * — oferecer um controle que a gravação vai recusar é pior que não oferecê-lo.
 *
 * **O botão de abrir precisa das duas coisas: a permissão e o Pedido liberado.** RAT solta não
 * existe (FR-48), e o serviço recusa com `400` `negocio_sem_pedido` — a tela não gasta essa ida: o
 * botão não aparece, e a frase ao lado diz que a RAT se abre sobre pedido entregue. É o mesmo par
 * controle-mais-razão que a ficha usa nos atos que o desfecho bloqueia.
 *
 * **A lista continua visível sem Pedido, e a assimetria é deliberada.** O número do Negócio pode
 * ter sido preenchido depois das reclamações — ou nunca —, e esconder as RATs existentes junto com
 * o botão apagaria da tela um registro que o FR-51 manda manter visível.
 *
 * **O status é `<select>`, e o `<select>` grava no `onChange`.** É o molde de `TrocaDeResponsavel`:
 * não há botão "Salvar" ao lado, porque a escolha **é** o ato — um segundo clique para confirmar
 * uma lista de três opções seria cerimônia. Reenviar o valor corrente é inofensivo por construção:
 * o serviço trata status repetido como no-op idempotente, e o corte local (`valor === atual`) evita
 * até a ida à rede.
 *
 * **Sem a permissão, o status vira texto** — e não um `<select>` desabilitado: um controle cinzento
 * convida ao clique e não explica nada, enquanto o rótulo diz o que a RAT é sem prometer o que não
 * se pode fazer.
 *
 * **Não há campo de dinheiro em árvore nenhuma, e não há o que esconder** (FR-62): a tabela `rat`
 * não tem `valor`, `custo` nem `margem` em coluna nenhuma, e o contrato tem schema fechado. É a
 * diferença estrutural para o cartão de Produtos, que precisa dos três estados de `textoDoValor`.
 *
 * **A indicação de garantia (6.4, FR-53) fica no cabeçalho do grupo, e não em cada linha.** Ela é
 * do **Pedido**, não da reclamação: as RATs do mesmo Pedido falam da mesma carga, entregue no mesmo
 * dia, e repeti-la por linha diria quatro vezes a mesma coisa. O serviço a manda igual em todas.
 *
 * **Três estados, e o terceiro não é o segundo.** Dentro, fora, e `garantia === null` — que é "não
 * foi possível calcular", porque ninguém registrou a data de entrega. Desenhar o nulo como "fora"
 * seria a tela afirmando o que o serviço não afirmou, e desenhá-lo como nada seria pior: quem lê
 * uma linha ausente conclui que não há garantia.
 *
 * **Três estados de leitura, e os três estão escritos** — a ordem é a de `ProdutosDoNegocio`:
 * `isError` primeiro (a mensagem do serviço, verbatim, em `role="alert"`), depois `isLoading`, e só
 * então o estado vazio. Sem o terceiro, "não há reclamação" e "ainda não sei" ficariam iguais.
 *
 * **Duas árvores, escolhidas por `useEhCelular()` — e não um `@media`.** No computador cada grupo é
 * uma tabela com cabeçalho; no celular, blocos com rótulo em cima do valor. A paridade da NFR-1 é
 * de **função**: abrir e mover o status existem nas duas.
 */

import { type MouseEvent, useId, useRef, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useSessao } from '@/modulos/identidade/sessao'
import { Dialogo } from '@/modulos/negocios/Dialogo'
import {
  type Rat,
  type StatusDaRat,
  useAbrirRat,
  useAlterarStatusDaRat,
  useRats,
} from '@/modulos/negocios/api'
import {
  DATA_E_HORA,
  ROTULO_DO_STATUS_DA_RAT,
  dataPuraPorExtenso,
  TAMANHO_DA_DESCRICAO_DO_PROBLEMA,
  TAMANHO_DO_NUMERO_DA_OP,
} from '@/modulos/negocios/apresentacao'
import { useEhCelular } from '@/shell/Shell'
import './rat-do-negocio.css'

/** O nome acessível da seção — é ele que faz a `<section>` virar uma `region`. */
const ID_DO_TITULO = 'rat-do-negocio-titulo'

const TITULO = 'RAT'

/**
 * O selo do cartão, e ele **descreve** em vez de prometer.
 *
 * Ele diz onde o trabalho acontece, que é a primeira dúvida de quem abre a reclamação: o CRM
 * registra e avisa (6.2), e o conserto é da assistência técnica, fora daqui. É a mesma frase do
 * subtítulo do modal do UX-DR12, e ela aparece duas vezes de propósito — quem já tem a RAT aberta
 * não vê o modal.
 */
const SELO = 'O reparo acontece fora do CRM'

const CARREGANDO = 'Carregando as RATs…'

/**
 * O estado vazio: não há reclamação **neste** Negócio, e de onde ela viria.
 *
 * Ele não anuncia épico nenhum — anunciar um dentro de um cartão que já entrega a funcionalidade
 * diria que ela não chegou. É a mesma troca que `SEM_TAREFAS` fez na 4.2 e o cartão de Orçamento
 * fez na 5.3, e ela é o que aposenta o `SEM_RAT` da 3.10.
 */
const VAZIO = 'Nenhuma RAT neste negócio. Ela nasce quando o cliente reclama de um pedido entregue.'

/**
 * A razão pela qual o botão de abrir não aparece sem Pedido — escrita **aqui**, e não adivinhada.
 *
 * O serviço responde `400` `negocio_sem_pedido` com a mensagem dele; esta é a da tela, e ela existe
 * para que o controle ausente tenha explicação ao lado em vez de sumir sem motivo. A ida à rede não
 * chega a acontecer: RAT solta não existe (FR-48), e o botão só nasce com o Pedido liberado.
 */
const SEM_PEDIDO =
  'Este negócio ainda não tem pedido liberado no Carga Máquina, e a RAT se abre sobre um pedido ' +
  'entregue. Ela fica disponível assim que o pedido for liberado.'

/** O rótulo do grupo — o Pedido é o que a operação diz na boca, e vem antes do número. */
const PEDIDO = 'Pedido'

/**
 * O nome acessível da tabela (ou da lista) **dentro** do grupo.
 *
 * Ele é diferente do da `<section>` em volta de propósito: dois nomes iguais aninhados fazem o
 * leitor de tela anunciar "Pedido X, região" e, no elemento seguinte, "Pedido X, tabela" — a
 * segunda locução não diz nada que a primeira já não tenha dito.
 */
const RECLAMACOES_DO = 'Reclamações do Pedido'

/** Os três estados do FR-53. O terceiro é frase inteira, e não um travessão: ver a docstring. */
const GARANTIA_DENTRO = 'Dentro da garantia'
const GARANTIA_FORA = 'Fora da garantia'
const GARANTIA_SEM_DATA = 'Não foi possível calcular a garantia: a data de entrega não foi registrada.'
const ENTREGUE_EM = 'Entregue em'
const VENCE_EM = 'vence em'
const VENCEU_EM = 'venceu em'

/** Os rótulos das colunas, escritos uma vez e usados nas duas árvores. */
const ROTULOS = {
  problema: 'Problema',
  op: 'OP',
  status: 'Status',
  aberta: 'Aberta em',
} as const

const ABRIR_RAT = 'Abrir RAT'

/** O rótulo do confirmar do modal, literal do UX-DR12. */
const SALVAR_E_AVISAR = 'Salvar e avisar por e-mail'

/**
 * O aviso do corpo do modal, literal do UX-DR12 — e ele é a promessa que a 6.2 cumpre.
 *
 * "Avisar por e-mail" não é figura de linguagem: o `POST` publica `rat_aberta` no outbox, e o
 * `comunicacao` manda o e-mail ao pós-venda (FR-50). O rótulo do botão diz o que vai acontecer, e
 * este parágrafo diz o que **não** vai — o conserto continua fora do CRM.
 */
const AVISO_DO_MODAL =
  'Presa ao pedido entregue. O reparo acontece fora do CRM: o aviso vai por e-mail ao pós-venda.'

const ROTULO_DO_PROBLEMA = 'Descrição do problema'
const ROTULO_DA_OP = 'Número da OP'

/**
 * A ajuda do campo da OP, e ela explica a **ausência** de uma lista.
 *
 * O número é de digitação livre por decisão fechada do PRD (FR-49): o ERP não expõe as OPs de um
 * Pedido, e um `<select>` vazio seria pior que um campo de texto. Sem esta frase, quem abre a RAT
 * procuraria a lista que não existe.
 */
const AJUDA_DA_OP = 'Digite o número da Ordem de Produção — não há lista automática de OPs.'

/**
 * A recusa **local** dos dois campos, e ela existe para o `422` genérico nunca ser gasto.
 *
 * Os dois são obrigatórios no contrato (`min_length=1` sobre o valor já aparado), e o corpo vazio
 * recebe a mensagem genérica do envelope — verdadeira e inútil, porque não diz qual campo. O
 * confirmar fica desabilitado enquanto qualquer um dos dois estiver em branco, e a frase ao lado
 * diz por quê.
 */
const CAMPOS_OBRIGATORIOS = 'Descreva o problema e informe a OP para abrir a RAT.'

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * As RATs agrupadas por `numero_do_pedido`, na ordem em que cada Pedido aparece na lista.
 *
 * O `Map` é o que preserva a ordem: um objeto com chaves de texto reordenaria os grupos pela regra
 * de propriedades inteiras do JavaScript, e "PED-2026-0001" e "PED-2026-0002" sairiam em ordem
 * alfabética em vez da ordem por recência que o serviço já decidiu.
 *
 * Ela é função pura de módulo, e não um `useMemo` dentro do componente, porque é assim que ela ganha
 * teste próprio: agrupar é a regra que sustenta o FR-51, e uma regra escondida em render só é
 * observável pelo DOM que ela produz.
 */
export function agruparPorPedido(rats: Rat[]): [string, Rat[]][] {
  const grupos = new Map<string, Rat[]>()
  for (const rat of rats) {
    const doPedido = grupos.get(rat.numero_do_pedido)
    if (doPedido === undefined) {
      grupos.set(rat.numero_do_pedido, [rat])
    } else {
      doPedido.push(rat)
    }
  }
  return [...grupos]
}

/**
 * O nome acessível do seletor de uma linha — e ele tem de ser **único na tela**.
 *
 * A OP sozinha não serve: `numero_da_op` é digitação livre (FR-49), sem `UNIQUE` na coluna e sem
 * unicidade nenhuma no domínio — duas reclamações sobre a mesma Ordem de Produção são o caso
 * normal, e dois comboboxes de nome idêntico deixam quem navega por controles sem saber qual está
 * mexendo.
 *
 * O que identifica de fato é o par **Pedido + posição no grupo**: cada grupo desenha uma lista, e a
 * posição dentro dela é única por construção. A OP fica como contexto entre parênteses, porque é
 * por ela que a produção reconhece a reclamação — tirá-la deixaria o rótulo correto e inútil.
 *
 * O `id` da linha resolveria a unicidade e não é usado: é um uuid, e um leitor de tela o soletraria
 * inteiro antes de dizer o que o controle faz.
 */
function nomeDoSeletor(rat: Rat, pedido: string, posicao: number): string {
  return `${ROTULOS.status} da RAT ${String(posicao)} do pedido ${pedido} (OP ${rat.numero_da_op})`
}

/**
 * O status de uma RAT: o `<select>` de quem pode mover, ou o rótulo de quem não pode.
 *
 * O `aria-busy` marca a gravação em voo — é o mesmo sinal de `TrocaDeResponsavel`, e ele existe
 * porque não há botão "Salvar" cujo `disabled` contasse a mesma coisa.
 *
 * O corte `valor === atual` evita a ida à rede quando o `<select>` reemite o valor que já está lá
 * (um `Esc` no menu aberto, por exemplo). Ele **não** é a regra: o serviço trata o repetido como
 * no-op idempotente, e é ele quem responde por qualquer chamada que escape daqui.
 */
function StatusDaLinha({
  rat,
  pedido,
  posicao,
  podeAlterar,
  emVoo,
  gravando,
  aoEscolher,
}: {
  rat: Rat
  /** O Pedido do grupo — metade do nome acessível, e o que o torna único entre grupos. */
  pedido: string
  /** A posição da linha dentro do grupo, a partir de 1 — a outra metade. */
  posicao: number
  podeAlterar: boolean
  /**
   * O status que **esta** linha está gravando agora, ou `undefined`.
   *
   * Ele é o valor otimista: enquanto o `PUT` está no ar, `rat.status` ainda é o antigo (a lista só
   * é reinvalidada no sucesso), e sem isto o `<select>` voltaria sozinho para a opção anterior no
   * quadro seguinte ao clique — quem escolheu "Resolvida" veria "Aberta" de novo e clicaria outra
   * vez. É o mesmo `gravacao.variables` de `TrocaDeResponsavel`.
   *
   * Ele é **por linha**, e não do cartão: `mutation.isPending` é um estado só para todas as RATs, e
   * usá-lo direto poria cada `<select>` da tela em `aria-busy` porque **uma** delas está gravando.
   */
  emVoo: StatusDaRat | undefined
  /**
   * Alguma linha do cartão está gravando — a guarda de reentrância, que é global de propósito.
   *
   * Global porque a mutação é **uma só** para o cartão inteiro: `mutation.variables` tem um slot,
   * e um segundo `PUT` no ar o sobrescreveria — a linha que gravava primeiro perderia o `emVoo`
   * dela e o `<select>` voltaria sozinho para o status antigo com a resposta ainda a caminho.
   * Não é a corrida entre dois `PUT` da **mesma** RAT que exige a globalidade; é o slot único.
   */
  gravando: boolean
  aoEscolher: (status: StatusDaRat) => void
}) {
  if (!podeAlterar) {
    return <span className="rat__situacao">{ROTULO_DO_STATUS_DA_RAT[rat.status]}</span>
  }
  const atual = emVoo ?? rat.status
  /*
   * Esta linha está travada por causa de **outra**: alguém grava, e não é ela.
   *
   * O `aria-disabled` existe para que a trava não seja silenciosa. Sem ele o `<select>` continua
   * com cara de disponível, a escolha é engolida pela guarda abaixo e o valor volta para o antigo
   * sem que nada diga por quê. `aria-disabled`, e não `disabled`: a trava dura o tempo de um `PUT`,
   * e `disabled` tiraria o controle da ordem de tabulação no meio da navegação de quem usa teclado.
   */
  const travadaPorOutra = gravando && emVoo === undefined
  return (
    <select
      className="rat__selecao"
      aria-label={nomeDoSeletor(rat, pedido, posicao)}
      aria-busy={emVoo === undefined ? undefined : true}
      aria-disabled={travadaPorOutra ? true : undefined}
      value={atual}
      onChange={(evento) => {
        const escolhido = evento.target.value as StatusDaRat
        /*
         * Dois cortes, e eles são de coisas diferentes — o molde é o de `TrocaDeResponsavel`.
         *
         * O primeiro é o valor repetido: o serviço já trata status igual ao atual como no-op
         * idempotente, e este corte só evita a ida à rede. O segundo é a **reentrância**: com uma
         * gravação no ar, um segundo `change` (o teclado percorrendo as opções, um duplo clique)
         * mandaria um `PUT` concorrente — e a mutação é uma só, de `variables` de slot único, então
         * a segunda chamada apagaria o `emVoo` da primeira antes de a resposta dela chegar.
         */
        if (escolhido === atual || gravando) {
          return
        }
        aoEscolher(escolhido)
      }}
    >
      {Object.entries(ROTULO_DO_STATUS_DA_RAT).map(([valor, rotulo]) => (
        <option key={valor} value={valor}>
          {rotulo}
        </option>
      ))}
    </select>
  )
}

export function RatDoNegocio({
  negocioId,
  numeroDoPedido,
  excluido = false,
  somenteLeitura = false,
}: {
  negocioId: string
  /**
   * O Pedido do Negócio — e ele decide **só** se o botão de abrir aparece.
   *
   * Ele chega da **ficha**, que já leu o campo: uma segunda leitura aqui gastaria uma consulta para
   * saber o que a tela em volta já sabe, e as duas poderiam discordar por um quadro de renderização.
   *
   * Ele **não** é usado para agrupar: quem agrupa é o `numero_do_pedido` de cada RAT, que é o
   * snapshot do instante da abertura. Usá-lo aqui faria as reclamações mudarem de grupo no dia em
   * que o número do Negócio mudasse.
   */
  numeroDoPedido: string | null
  /**
   * O Negócio foi excluído (3.5) — e aí a leitura das RATs é **desligada**.
   *
   * A exclusão é real e a FK é `CASCADE`: as reclamações somem junto, e `GET .../rats` passa a
   * responder `404`. Sem esta prop o cartão pediria a lista de novo no caminho de saída — o
   * `useExcluirNegocio` invalida o prefixo `CHAVE_DOS_NEGOCIOS`, que alcança `chaveDasRats` —, e a
   * ficha desenharia a recusa por um quadro antes de desmontar. É a mesma razão pela qual a ficha
   * desliga `useFicha` e `useMovimentacoes`, e ela chega daqui pelo mesmo caminho: quem sabe da
   * exclusão é a tela que a disparou.
   *
   * O padrão é `false` para que o cartão continue montável fora da ficha.
   */
  excluido?: boolean
  /** Quem lê o Negócio só pela tarefa aberta (13.14c, DW-527): a PA-3 só lê `rat`. */
  somenteLeitura?: boolean
}) {
  const ehCelular = useEhCelular()
  const { data: sessao } = useSessao()
  const consulta = useRats(negocioId, { habilitada: !excluido })
  const abertura = useAbrirRat(negocioId)
  const mudanca = useAlterarStatusDaRat(negocioId)

  const [aberto, setAberto] = useState(false)
  const [problema, setProblema] = useState('')
  const [op, setOp] = useState('')

  const idDoProblema = useId()
  const idDaOp = useId()
  const idDaAjudaDaOp = useId()
  const idDaRecusa = useId()

  /*
   * O botão que abriu o modal, para devolver o foco a ele quando o modal sair.
   *
   * É o mesmo cuidado que a ficha toma com os três diálogos dela: sem isto o foco cairia no `body`,
   * e quem navega por teclado teria de tabular a tela inteira para voltar onde estava. `useRef`, e
   * não estado: guardá-lo em `useState` re-renderizaria o cartão só para lembrar de um elemento que
   * ninguém desenha.
   */
  const gatilho = useRef<HTMLElement | null>(null)

  /*
   * A permissão é **conveniência**, e não autorização: quem decide é o serviço, que responde `403`
   * a quem não a tem. A presença na claim basta — só as não negadas viajam, e `limitada` passa pela
   * guarda da rota (o recorte de `limitada` é de linha, e não de rota). É o molde de `podeEditar`.
   */
  /* 13.14c (DW-527): quem lê só pela tarefa aberta não abre nem muda RAT. */
  const podeAbrir = sessao !== undefined && 'abrir_rat' in sessao.permissoes && !somenteLeitura
  const podeAlterar =
    sessao !== undefined && 'alterar_status_da_rat' in sessao.permissoes && !somenteLeitura

  const lista = consulta.data?.itens ?? []
  const grupos = agruparPorPedido(lista)
  const camposPreenchidos = problema.trim() !== '' && op.trim() !== ''

  function abrirODialogo(evento: MouseEvent<HTMLButtonElement>) {
    gatilho.current = evento.currentTarget
    abertura.reset()
    setProblema('')
    setOp('')
    setAberto(true)
  }

  function fecharODialogo() {
    setAberto(false)
    gatilho.current?.focus()
    gatilho.current = null
  }

  function mover(rat: Rat, status: StatusDaRat) {
    mudanca.mutate({ ratId: rat.id, status })
  }

  /*
   * O estado da mutação é do **cartão**, e o que a tela precisa é por **linha** — estas duas
   * funções são a tradução de um para o outro.
   *
   * `mutation.variables` guarda o argumento da última chamada, e é ele que diz de qual RAT o
   * estado corrente fala. Sem esse recorte, mover uma reclamação poria todos os `<select>` do
   * cartão em `aria-busy` e esmaecidos, e a recusa apareceria num `role="alert"` no rodapé — sem
   * dizer qual das linhas falhou, que é justamente o que quem tem seis RATs precisa saber.
   *
   * `variables` sobrevive ao fim da mutação (o react-query só o troca na chamada seguinte), e por
   * isso as duas funções conferem `isPending`/`isError` **junto** com o `ratId`: sem o par, a linha
   * que gravou por último ficaria marcada para sempre.
   */
  const gravandoNaLinha = (rat: Rat): StatusDaRat | undefined =>
    mudanca.isPending && mudanca.variables?.ratId === rat.id ? mudanca.variables.status : undefined

  const falhaDaLinha = (rat: Rat): unknown =>
    mudanca.isError && mudanca.variables?.ratId === rat.id ? mudanca.error : undefined

  /** A recusa da mudança, desenhada **dentro** da linha que a produziu. */
  const erroDaLinha = (rat: Rat) => {
    const falha = falhaDaLinha(rat)
    if (falha === undefined) {
      return null
    }
    /* Verbatim: o `404` do par errado e o `403` de quem perdeu a permissão entre o desenho e o
       clique chegam com a frase do serviço. */
    return (
      <p className="ficha__erro rat__erro-da-linha" role="alert">
        {mensagemDe(falha)}
      </p>
    )
  }

  /** Uma linha na árvore de mesa — a célula de status é a mesma dos dois lados. */
  /**
   * A indicação de garantia do grupo (FR-53) — lida da **primeira** RAT dele.
   *
   * Ler de uma só não é atalho: a garantia é do Negócio, o serviço a deriva uma vez por leitura e
   * a repete idêntica em todas as RATs. Percorrer as quatro para confirmar que concordam seria
   * desconfiar do contrato dentro da tela.
   */
  const indicacaoDaGarantia = (rats: Rat[]) => {
    const garantia = rats[0]?.garantia ?? null
    if (garantia === null) {
      return <p className="rat__garantia rat__garantia--sem-data">{GARANTIA_SEM_DATA}</p>
    }
    return (
      <p
        className={`rat__garantia ${garantia.dentro ? 'rat__garantia--dentro' : 'rat__garantia--fora'}`}
      >
        <strong>{garantia.dentro ? GARANTIA_DENTRO : GARANTIA_FORA}</strong>
        {' · '}
        {ENTREGUE_EM} {dataPuraPorExtenso(garantia.entregue_em)}
        {', '}
        {garantia.dentro ? VENCE_EM : VENCEU_EM} {dataPuraPorExtenso(garantia.vence_em)}
      </p>
    )
  }

  const tabelaDoGrupo = (rats: Rat[], pedido: string) => (
    /* O `aria-label` dá à tabela o mesmo nome que a `<ul>` do celular já tem: sem ele, quem navega
       por tabelas encontra tabelas anônimas empilhadas dentro do cartão. Ele **não** repete o nome
       da `<section>` em volta: nomes iguais aninhados fazem o leitor de tela anunciar "Pedido X,
       região" e logo em seguida "Pedido X, tabela", e a segunda locução não acrescenta nada. */
    <table className="rat__tabela" aria-label={`${RECLAMACOES_DO} ${pedido}`}>
      <thead>
        <tr>
          <th scope="col">{ROTULOS.problema}</th>
          <th scope="col">{ROTULOS.op}</th>
          <th scope="col">{ROTULOS.aberta}</th>
          <th scope="col">{ROTULOS.status}</th>
        </tr>
      </thead>
      <tbody>
        {rats.map((rat, posicao) => (
          <tr key={rat.id}>
            <td className="rat__problema">{rat.descricao_do_problema}</td>
            <td>{rat.numero_da_op}</td>
            <td>{DATA_E_HORA.format(new Date(rat.criado_em))}</td>
            <td>
              <StatusDaLinha
                rat={rat}
                pedido={pedido}
                posicao={posicao + 1}
                podeAlterar={podeAlterar}
                emVoo={gravandoNaLinha(rat)}
                gravando={mudanca.isPending}
                aoEscolher={(status) => {
                  mover(rat, status)
                }}
              />
              {/* A recusa mora **na célula da linha que falhou**, e não no rodapé do cartão: com
                  seis RATs na tela, um alerta lá embaixo diria que algo deu errado sem dizer com
                  qual reclamação. */}
              {erroDaLinha(rat)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  /** O mesmo grupo no celular: blocos com o rótulo em cima do valor. */
  const blocosDoGrupo = (rats: Rat[], pedido: string) => (
    <ul className="rat__linhas" aria-label={`${RECLAMACOES_DO} ${pedido}`}>
      {rats.map((rat, posicao) => (
        <li className="rat__linha" key={rat.id}>
          <span className="rat__problema">{rat.descricao_do_problema}</span>
          <div className="rat__campos">
            <span className="rat__par">
              <span className="rat__rotulo">{ROTULOS.op}</span>
              <span className="rat__campo">{rat.numero_da_op}</span>
            </span>
            <span className="rat__par">
              <span className="rat__rotulo">{ROTULOS.aberta}</span>
              <span className="rat__campo">{DATA_E_HORA.format(new Date(rat.criado_em))}</span>
            </span>
            <span className="rat__par">
              <span className="rat__rotulo">{ROTULOS.status}</span>
              <span className="rat__campo">
                <StatusDaLinha
                  rat={rat}
                  pedido={pedido}
                  posicao={posicao + 1}
                  podeAlterar={podeAlterar}
                  emVoo={gravandoNaLinha(rat)}
                  gravando={mudanca.isPending}
                  aoEscolher={(status) => {
                    mover(rat, status)
                  }}
                />
              </span>
            </span>
          </div>
          {/* A recusa mora **dentro do bloco da linha**, como na árvore de mesa: a paridade da
              NFR-1 vale para saber o que falhou tanto quanto para poder agir. */}
          {erroDaLinha(rat)}
        </li>
      ))}
    </ul>
  )

  const corpoDaLista = consulta.isError ? (
    /* Verbatim: a recusa do serviço é a que sabe explicar o caso — inclusive o `404` do Negócio
       que a RLS não entrega, que é o mesmo do inexistente. */
    <p className="ficha__erro" role="alert">
      {mensagemDe(consulta.error)}
    </p>
  ) : /* `|| consulta.data === undefined` fecha o terceiro estado: sem rede, a consulta fica
        **pausada** — nem erro, nem `isLoading` —, e sem esta metade a tela desenharia o estado
        vazio como se o Negócio não tivesse reclamação. */
  consulta.isLoading || consulta.data === undefined ? (
    <p className="ficha__ajuda" role="status">
      {CARREGANDO}
    </p>
  ) : grupos.length === 0 ? (
    <p className="ficha__vazio">{VAZIO}</p>
  ) : (
    <div className="rat__grupos">
      {grupos.map(([pedido, rats]) => (
        <section className="rat__grupo" key={pedido} aria-label={`${PEDIDO} ${pedido}`}>
          {/* O cabeçalho do grupo é o **Pedido**, e é ele que faz "as RATs de cada um
              separadamente" (FR-51) ser legível: sem o rótulo, dois blocos empilhados pareceriam
              uma lista só quebrada por acaso. */}
          <p className="rat__pedido">
            <span className="rat__pedido-rotulo">{PEDIDO}</span>
            <span className="rat__pedido-numero">{pedido}</span>
          </p>
          {/* A garantia vem logo abaixo do Pedido porque é sobre ele que ela fala — e antes das
              reclamações porque é o que a pessoa precisa saber **antes** de ler a primeira. */}
          {indicacaoDaGarantia(rats)}
          {ehCelular ? blocosDoGrupo(rats, pedido) : tabelaDoGrupo(rats, pedido)}
        </section>
      ))}
    </div>
  )

  return (
    <section className="ficha__card" aria-labelledby={ID_DO_TITULO}>
      <div className="ficha__card-topo">
        <span className="ficha__card-titulo" id={ID_DO_TITULO}>
          {TITULO}
        </span>
        <span className="ficha__selo">{SELO}</span>
      </div>
      <div className="ficha__card-corpo">
        {corpoDaLista}

        {/* O botão precisa das **duas** coisas: a permissão e o Pedido liberado. Sem a primeira
            não há o que oferecer; sem o segundo, o serviço recusaria com `400` — e a frase abaixo
            é o que explica a ausência em vez de deixar o controle sumir sem motivo. */}
        {podeAbrir && numeroDoPedido !== null ? (
          <button
            type="button"
            className="ficha__botao ficha__botao--primario"
            onClick={abrirODialogo}
          >
            {ABRIR_RAT}
          </button>
        ) : podeAbrir ? (
          <p className="ficha__ajuda">{SEM_PEDIDO}</p>
        ) : null}

        {/* **Não há alerta de mudança de status aqui**, e a ausência é a decisão: a recusa é
            desenhada dentro da linha que a produziu (`erroDaLinha`). Um `role="alert"` no rodapé
            do cartão diria que algo falhou sem dizer com qual das reclamações. */}
      </div>

      {/* O `numeroDoPedido !== null` aqui é o **estreitamento**, e não uma segunda guarda: quem
          abre o modal é o botão acima, que já o exige. Ele está escrito para que o subtítulo não
          precise do `?? ''` que tinha — o fallback vazio nunca seria alcançado hoje, e no dia em
          que a guarda de cima afrouxasse ele escreveria "Pedido " com o rótulo pendurado, calado. */}
      {aberto && numeroDoPedido !== null ? (
        <Dialogo
          titulo={ABRIR_RAT}
          /* O subtítulo é o Pedido: é ele que a reclamação vai carregar, e vê-lo antes de salvar é
             o que evita abrir a RAT sobre o pedido errado. */
          subtitulo={`${PEDIDO} ${numeroDoPedido}`}
          rotuloDoConfirmar={SALVAR_E_AVISAR}
          confirmarDesabilitado={!camposPreenchidos}
          ocupado={abertura.isPending}
          erro={abertura.isError ? abertura.error : undefined}
          aoFechar={fecharODialogo}
          aoConfirmar={() => {
            if (!camposPreenchidos) {
              return
            }
            abertura.mutate(
              { descricao_do_problema: problema.trim(), numero_da_op: op.trim() },
              { onSuccess: fecharODialogo },
            )
          }}
        >
          <p className="ficha__dialogo-aviso">{AVISO_DO_MODAL}</p>
          <label className="ficha__linha-rotulo" htmlFor={idDoProblema}>
            {ROTULO_DO_PROBLEMA}
          </label>
          {/* `maxLength` é o teto da coluna, importado e não digitado: um teto a mais viraria
              violação de coluna no commit, e a recusa apareceria como `500`. */}
          <textarea
            id={idDoProblema}
            className="ficha__entrada rat__texto"
            rows={4}
            maxLength={TAMANHO_DA_DESCRICAO_DO_PROBLEMA}
            aria-describedby={camposPreenchidos ? undefined : idDaRecusa}
            value={problema}
            onChange={(evento) => {
              setProblema(evento.target.value)
            }}
          />
          <label className="ficha__linha-rotulo" htmlFor={idDaOp}>
            {ROTULO_DA_OP}
          </label>
          <input
            id={idDaOp}
            className="ficha__entrada"
            type="text"
            autoComplete="off"
            maxLength={TAMANHO_DO_NUMERO_DA_OP}
            aria-describedby={idDaAjudaDaOp}
            value={op}
            onChange={(evento) => {
              setOp(evento.target.value)
            }}
          />
          <p className="ficha__ajuda" id={idDaAjudaDaOp}>
            {AJUDA_DA_OP}
          </p>
          {camposPreenchidos ? null : (
            <p className="ficha__ajuda" id={idDaRecusa}>
              {CAMPOS_OBRIGATORIOS}
            </p>
          )}
        </Dialogo>
      ) : null}
    </section>
  )
}
